import React, { useEffect, useState } from 'react';
import { ThumbsUp, ThumbsDown, Check, Loader2 } from 'lucide-react';
import { articleFeedbackApi } from '../../lib/api';

const TOKENS = {
  lime: '#C7FA3E',
  limeBorder: 'rgba(199, 250, 62, 0.40)',
  surface1: '#15151A',
};

const SESSION_KEY = 'ninja_session_id';
const voteKeyFor = (slug) => `article_feedback_${slug}`;

const getSessionId = () => {
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch (e) {
    // localStorage disabled — generate a per-page-load id
    return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  }
};

const savedVoteFor = (slug) => {
  try {
    const raw = localStorage.getItem(voteKeyFor(slug));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const persistVote = (slug, vote) => {
  try {
    localStorage.setItem(voteKeyFor(slug), JSON.stringify({ vote, ts: Date.now() }));
  } catch (e) {
    // ignore
  }
};

/**
 * "Was this helpful?" widget — seeds aggregate_rating from reader votes.
 * Once article accumulates ≥5 votes, ratings surface as stars in Google SERP.
 */
export const ArticleFeedbackWidget = ({ slug }) => {
  const [status, setStatus] = useState('idle'); // 'idle' | 'voting' | 'commenting' | 'done'
  const [vote, setVote] = useState(null); // 'up' | 'down' | null
  const [comment, setComment] = useState('');
  const [stats, setStats] = useState(null);

  // Restore prior vote from localStorage
  useEffect(() => {
    const prior = savedVoteFor(slug);
    if (prior?.vote) {
      setVote(prior.vote);
      setStatus('done');
    }
  }, [slug]);

  const submitVote = async (helpful) => {
    setStatus('voting');
    const sessionId = getSessionId();
    try {
      const res = await articleFeedbackApi.submit(slug, {
        session_id: sessionId,
        helpful,
      });
      setStats(res.data);
      const nextVote = helpful ? 'up' : 'down';
      setVote(nextVote);
      persistVote(slug, nextVote);
      // For 👎, prompt for the "why" comment before finalizing
      setStatus(helpful ? 'done' : 'commenting');
    } catch (e) {
      // Fail silently — widget just returns to idle
      setStatus('idle');
    }
  };

  const submitComment = async () => {
    if (!comment.trim()) {
      setStatus('done');
      return;
    }
    setStatus('voting');
    const sessionId = getSessionId();
    try {
      await articleFeedbackApi.submit(slug, {
        session_id: sessionId,
        helpful: false,
        comment: comment.trim(),
      });
    } catch (e) {
      // ignore — vote already counted, comment is optional
    }
    setStatus('done');
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <section
      className="my-12 p-6 border"
      style={{ backgroundColor: TOKENS.surface1, borderColor: TOKENS.limeBorder }}
      data-testid="article-feedback-widget"
    >
      {status === 'idle' && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p
            className="text-white text-lg"
            style={{ fontFamily: 'Anton, Archivo Narrow, Bebas Neue, sans-serif' }}
          >
            Was this article helpful?
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => submitVote(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold uppercase tracking-wider text-black transition-all duration-200 hover:-translate-y-0.5"
              style={{ backgroundColor: TOKENS.lime, fontFamily: 'Anton, sans-serif', letterSpacing: '0.08em' }}
              data-testid="feedback-helpful-btn"
            >
              <ThumbsUp className="w-4 h-4" /> Yes, helpful
            </button>
            <button
              type="button"
              onClick={() => submitVote(false)}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold uppercase tracking-wider text-white bg-transparent border border-zinc-700 hover:border-zinc-500 transition-all duration-200"
              style={{ fontFamily: 'Anton, sans-serif', letterSpacing: '0.08em' }}
              data-testid="feedback-unhelpful-btn"
            >
              <ThumbsDown className="w-4 h-4" /> Not really
            </button>
          </div>
        </div>
      )}

      {status === 'voting' && (
        <div className="flex items-center gap-3 text-zinc-400 text-sm" data-testid="feedback-loading">
          <Loader2 className="w-4 h-4 animate-spin" />
          Recording your feedback…
        </div>
      )}

      {status === 'commenting' && (
        <div data-testid="feedback-comment-form">
          <p className="text-white text-base mb-3" style={{ fontFamily: 'Anton, sans-serif' }}>
            Thanks — what could we improve?
          </p>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Optional — one line is fine"
            className="w-full bg-black border border-zinc-800 text-white text-sm p-3 focus:outline-none focus:border-zinc-500 resize-none"
            data-testid="feedback-comment-input"
          />
          <div className="flex items-center justify-between mt-3">
            <span className="text-zinc-600 text-xs">{comment.length}/500</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setStatus('done')}
                className="text-zinc-500 hover:text-zinc-300 text-xs uppercase tracking-wider px-3 py-2"
                data-testid="feedback-skip-btn"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={submitComment}
                className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider text-black font-semibold"
                style={{ backgroundColor: TOKENS.lime, fontFamily: 'Anton, sans-serif', letterSpacing: '0.08em' }}
                data-testid="feedback-submit-comment-btn"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {status === 'done' && (
        <div className="flex items-center gap-3" data-testid="feedback-thanks">
          <div
            className="w-8 h-8 flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: TOKENS.lime }}
          >
            <Check className="w-4 h-4 text-black" strokeWidth={3} />
          </div>
          <div>
            <p className="text-white text-sm font-medium">
              Thanks for the feedback.
            </p>
            {stats && stats.total >= 5 && (
              <p className="text-zinc-500 text-xs mt-0.5" data-testid="feedback-stats-line">
                {stats.helpful} of {stats.total} readers found this helpful
                {stats.rating ? ` · ${stats.rating.toFixed(1)}★` : ''}
              </p>
            )}
            {vote === 'down' && !comment && (
              <p className="text-zinc-500 text-xs mt-0.5">
                We&apos;ll use this to make the guide sharper.
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default ArticleFeedbackWidget;
